import * as React from 'react';
import * as moment from 'moment';
import { FormGroup, ControlLabel, FormControl, Button } from 'react-bootstrap';
import { IListItem } from '@nkr/models/index';
import { IOrderRequest } from '@nkr/models/order-request';
import { OrganType } from '@nkr/enums/index';

interface IOptionDef {
    key: string;
    label: string;
    ids: number[] | null;
}

const KIT_TYPE_OPTIONS: IOptionDef[] = [
    { key: 'all',         label: 'All Kit Types',              ids: null },
    { key: 'pwl',         label: 'Pre-Workup Lab',             ids: [0] },
    { key: 'hbd',         label: 'Home Blood Draw',            ids: [1] },
    { key: 'sserology',   label: 'Streamlined Serology',       ids: [11] },
    { key: 'dserology',   label: 'Direct Streamlined Serology', ids: [4] },
    { key: 'donorcheek',  label: 'Donor Cheek Swab',           ids: [7] },
    { key: 'recpcheek',   label: 'Recipient Cheek Swab',       ids: [8] },
    { key: 'antibody',    label: 'Antibody',                   ids: [9] },
    { key: 'finalxm',     label: 'Final XM',                   ids: [10] },
    { key: 'bulkcheek',   label: 'Bulk Cheek',                 ids: [13] },
    { key: 'sb',          label: 'Strongbox',                  ids: [14] },
    { key: 'sbopo',       label: 'Strongbox OPO',          ids: [15] },
];

const LIVER_KIT_TYPE_OPTIONS: IOptionDef[] = [
    { key: 'all',         label: 'All Kit Types',              ids: null },
    { key: 'sserology',   label: 'Streamlined Serology',       ids: [11] },
    { key: 'dserology',   label: 'Direct Streamlined Serology', ids: [4] },
    { key: 'hbd',         label: 'Home Blood Draw',            ids: [1] },
];

const STATUS_OPTIONS: IOptionDef[] = [
    { key: 'all',       label: 'All Statuses', ids: null },
    { key: 'pending',   label: 'Pending',      ids: [-2] },
    { key: 'ordered',   label: 'Ordered',      ids: [1] },
    { key: 'intransit', label: 'In Transit',   ids: [2] },
    { key: 'received',  label: 'Received',     ids: [3] },
    { key: 'returned',  label: 'Returned',     ids: [4, 5] },
    { key: 'cancelled', label: 'Cancelled',    ids: [0] },
];

interface IProps {
    organType: OrganType;
    kitTypes: IListItem[];
    shipVendors: IListItem[];
    orderStatuses: IListItem[];
    onSearch: (orderRequest: Partial<IOrderRequest>) => void;
    onDateChange: (startDate: string, endDate: string, dateType: number) => void;
}

interface IState {
    startDate: string;
    endDate: string;
    dateType: number;
    activeKitTypeKey: string;
    activeStatusKey: string;
    donorAlias: string;
    patientAlias: string;
    swapNumber: string;
}

export default class TrackingSearchBar extends React.Component<IProps, IState> {
    readonly state: IState = {
        startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        dateType: 1,
        activeKitTypeKey: 'all',
        activeStatusKey: 'all',
        donorAlias: '',
        patientAlias: '',
        swapNumber: '',
    };

    componentDidMount = () => {
        this.props.onDateChange(this.state.startDate, this.state.endDate, this.state.dateType);
        this.emitSearch();
    }

    handleSearch = () => {
        const { startDate, endDate, dateType } = this.state;
        this.props.onDateChange(startDate, endDate, dateType);
        this.emitSearch();
    }

    handleKitTypeChange = (e: React.FormEvent<FormControl>) => {
        this.setState({ activeKitTypeKey: (e.target as HTMLSelectElement).value }, this.emitSearch);
    }

    handleStatusChange = (e: React.FormEvent<FormControl>) => {
        this.setState({ activeStatusKey: (e.target as HTMLSelectElement).value }, this.emitSearch);
    }

    handleTextInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = event.currentTarget;
        this.setState({ [name]: value } as any, this.emitSearch);
    }

    emitSearch = () => {
        const { activeKitTypeKey, activeStatusKey, donorAlias, patientAlias, swapNumber } = this.state;
        const searchCriteria: Partial<IOrderRequest> = {};

        const allKitOptions = [...KIT_TYPE_OPTIONS, ...LIVER_KIT_TYPE_OPTIONS];
        const kitOption = allKitOptions.find(o => o.key === activeKitTypeKey);
        if (kitOption?.ids?.length > 0) {
            searchCriteria.entityTypeIds = kitOption.ids;
        }

        const statusOption = STATUS_OPTIONS.find(o => o.key === activeStatusKey);
        if (statusOption?.ids?.length > 0) {
            searchCriteria.statusIds = statusOption.ids;
        }

        if (donorAlias)   searchCriteria.donorAlias   = donorAlias;
        if (patientAlias) searchCriteria.patientAlias = patientAlias;
        if (swapNumber)   searchCriteria.swapNumber   = swapNumber;

        if (this.props.organType === OrganType.Liver) {
            searchCriteria.organType = OrganType.Liver;
        }

        this.props.onSearch(searchCriteria);
    }

    render() {
        const {
            startDate, endDate, dateType,
            activeKitTypeKey, activeStatusKey,
            donorAlias, patientAlias, swapNumber,
        } = this.state;
        const isLiver = this.props.organType === OrganType.Liver;
        const kitOptions = isLiver ? LIVER_KIT_TYPE_OPTIONS : KIT_TYPE_OPTIONS;

        return (
            <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '10px', paddingLeft: 'inherit', marginBottom: '10px' }}>

                <FormGroup controlId="startDate" style={{ margin: 0, minWidth: '120px' }}>
                    <ControlLabel>Start Date</ControlLabel>
                    <FormControl
                        type="date"
                        value={startDate}
                        onChange={(e: any) => this.setState({ startDate: e.target.value })}
                    />
                </FormGroup>

                <FormGroup controlId="endDate" style={{ margin: 0, minWidth: '120px' }}>
                    <ControlLabel>End Date</ControlLabel>
                    <FormControl
                        type="date"
                        value={endDate}
                        onChange={(e: any) => this.setState({ endDate: e.target.value })}
                    />
                </FormGroup>

                <FormGroup controlId="dateType" style={{ margin: 0, minWidth: '140px' }}>
                    <ControlLabel>Date Type</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={dateType}
                        onChange={(e: any) => this.setState({ dateType: Number(e.target.value) })}
                    >
                        <option value={1}>Request Date</option>
                        <option value={2}>Last Tracking Update</option>
                    </FormControl>
                </FormGroup>

                <FormGroup controlId="kitTypeFilter" style={{ margin: 0, minWidth: '145px' }}>
                    <ControlLabel>Kit Type</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={activeKitTypeKey}
                        onChange={this.handleKitTypeChange}
                    >
                        {kitOptions.map(o => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                    </FormControl>
                </FormGroup>

                <FormGroup controlId="statusFilter" style={{ margin: 0, minWidth: '130px' }}>
                    <ControlLabel>Status</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={activeStatusKey}
                        onChange={this.handleStatusChange}
                    >
                        {STATUS_OPTIONS.map(o => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                    </FormControl>
                </FormGroup>

                <FormGroup controlId="donorAlias" style={{ margin: 0, minWidth: '110px' }}>
                    <ControlLabel>Donor Alias</ControlLabel>
                    <FormControl
                        type="text"
                        name="donorAlias"
                        value={donorAlias}
                        onChange={this.handleTextInputChange as any}
                    />
                </FormGroup>

                <FormGroup controlId="patientAlias" style={{ margin: 0, minWidth: '120px' }}>
                    <ControlLabel>Recipient Alias</ControlLabel>
                    <FormControl
                        type="text"
                        name="patientAlias"
                        value={patientAlias}
                        onChange={this.handleTextInputChange as any}
                    />
                </FormGroup>

                {!isLiver && (
                    <FormGroup controlId="swapNumber" style={{ margin: 0, minWidth: '80px' }}>
                        <ControlLabel>Swap #</ControlLabel>
                        <FormControl
                            type="text"
                            name="swapNumber"
                            value={swapNumber}
                            onChange={this.handleTextInputChange as any}
                        />
                    </FormGroup>
                )}

                <FormGroup style={{ margin: 0, marginLeft: 'auto' }}>
                    <ControlLabel style={{ display: 'block' }}>&nbsp;</ControlLabel>
                    <Button bsStyle="primary" onClick={this.handleSearch}>Search</Button>
                </FormGroup>

            </div>
        );
    }
}
